// src/types/PurchaseOrder.ts

export enum PurchaseOrderStatus {
  DRAFT = 'draft',
  PENDING = 'pending',
  APPROVED = 'approved',
  ORDERED = 'ordered',
  RECEIVED = 'received',
  CANCELLED = 'cancelled',
}

export enum PaymentStatus {
  UNPAID = 'unpaid',
  PARTIAL = 'partial',
  PAID = 'paid',
}

export enum ReturnStatus {
  PENDING = 'pending',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum ReturnReason {
  DAMAGED = 'damaged',
  DEFECTIVE = 'defective',
  WRONG_ITEM = 'wrong_item',
  QUALITY_ISSUE = 'quality_issue',
  EXPIRED = 'expired',
  OTHER = 'other',
}

// A PO line can be a catalogue product OR a raw material / other supply that is
// entered free-form (not tied to the products collection).
export type PurchaseOrderItemType = 'product' | 'raw_material';

export interface PurchaseOrderItem {
  id?: string;
  // 'product' (default, backward compatible) or 'raw_material'
  itemType?: PurchaseOrderItemType;
  productId: string;      // for raw materials: '' or the rawMaterials master id
  productName: string;    // for raw materials: the material name
  variantId?: string;
  variantName?: string;
  sku: string;
  // Unit of measure for raw materials (e.g. kg, m, pcs). Optional for products.
  unit?: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  receivedQuantity: number;
  notes?: string;
  // Warehouse allocation for received items
  warehouseId?: string;
  warehouseName?: string;
}

export interface ReturnItem {
  id?: string;
  productId: string;
  productName: string;
  variantId?: string;
  variantName?: string;
  sku: string;
  quantity: number;
  unitPrice: number;
  totalPrice: number;
  reason: ReturnReason;
  notes?: string;
  warehouseId?: string;
  warehouseName?: string;
  images?: string[]; // Evidence photos
}

export interface PurchaseOrderReturn {
  id?: string;
  returnNumber: string;
  purchaseOrderId: string;
  purchaseOrderNumber: string;
  supplierId: string;
  supplierName: string;
  supplierEmail: string;
  supplierPhone: string;
  status: ReturnStatus;
  items: ReturnItem[];
  totalAmount: number;
  refundAmount: number;
  refundIssued: boolean;
  reason: string;
  notes?: string;
  internalNotes?: string;
  images?: string[]; // Supporting documentation
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  completedBy?: string;
  completedByName?: string;
  completedAt?: Date;
  // Flag to track if inventory has been adjusted
  inventoryAdjusted?: boolean;
}

export interface PurchaseOrderReturnFormData {
  purchaseOrderId: string;
  items: ReturnItem[];
  reason: string;
  notes: string;
  internalNotes: string;
  images?: File[];
}

export interface ReturnStats {
  total: number;
  pending: number;
  approved: number;
  completed: number;
  rejected: number;
  cancelled: number;
  totalValue: number;
  totalRefunded: number;
}

export interface Supplier {
  id?: string;
  name: string;
  email: string;
  phone: string;
  address: string;
  contactPerson?: string;
  taxId?: string;
  paymentTerms?: string;
  isActive: boolean;
  createdAt?: Date;
  updatedAt?: Date;
}

export interface PurchaseOrder {
  id?: string;
  poNumber: string;
  supplierId: string;
  supplierName: string;
  supplierEmail: string;
  supplierPhone: string;
  status: PurchaseOrderStatus;
  paymentStatus: PaymentStatus;
  items: PurchaseOrderItem[];
  subtotal: number;
  taxRate: number;
  taxAmount: number;
  shippingCost: number;
  totalAmount: number;
  paidAmount: number;
  expectedDeliveryDate?: Date;
  actualDeliveryDate?: Date;
  notes?: string;
  internalNotes?: string;
  createdAt: Date;
  updatedAt: Date;
  createdBy: string;
  createdByName: string;
  approvedBy?: string;
  approvedByName?: string;
  approvedAt?: Date;
  receivedBy?: string;
  receivedByName?: string;
  receivedAt?: Date;
  // Flag to track if stock has been updated
  stockUpdated?: boolean;
  // Return tracking
  hasReturns?: boolean;
  returnIds?: string[];
}

export interface PurchaseOrderFormData {
  supplierId: string;
  items: PurchaseOrderItem[];
  taxRate: string;
  shippingCost: string;
  expectedDeliveryDate: Date | null;
  notes: string;
  internalNotes: string;
}

export interface PurchaseOrderStats {
  total: number;
  draft: number;
  pending: number;
  approved: number;
  ordered: number;
  received: number;
  cancelled: number;
  totalValue: number;
  totalPaid: number;
  totalOutstanding: number;
}

export const PO_STATUS_COLORS: Record<PurchaseOrderStatus, string> = {
  [PurchaseOrderStatus.DRAFT]: 'default',
  [PurchaseOrderStatus.PENDING]: 'warning',
  [PurchaseOrderStatus.APPROVED]: 'info',
  [PurchaseOrderStatus.ORDERED]: 'primary',
  [PurchaseOrderStatus.RECEIVED]: 'success',
  [PurchaseOrderStatus.CANCELLED]: 'error',
};

export const PAYMENT_STATUS_COLORS: Record<PaymentStatus, string> = {
  [PaymentStatus.UNPAID]: 'error',
  [PaymentStatus.PARTIAL]: 'warning',
  [PaymentStatus.PAID]: 'success',
};

export const RETURN_STATUS_COLORS: Record<ReturnStatus, string> = {
  [ReturnStatus.PENDING]: 'warning',
  [ReturnStatus.APPROVED]: 'info',
  [ReturnStatus.REJECTED]: 'error',
  [ReturnStatus.COMPLETED]: 'success',
  [ReturnStatus.CANCELLED]: 'default',
};

export const RETURN_REASON_LABELS: Record<ReturnReason, string> = {
  [ReturnReason.DAMAGED]: 'Damaged',
  [ReturnReason.DEFECTIVE]: 'Defective',
  [ReturnReason.WRONG_ITEM]: 'Wrong Item',
  [ReturnReason.QUALITY_ISSUE]: 'Quality Issue',
  [ReturnReason.EXPIRED]: 'Expired',
  [ReturnReason.OTHER]: 'Other',
};